import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/native';
import { User, KeyRound, Bell, MapPin, Camera, Scale, FileText, LifeBuoy, Mail, Trash2, Info, ChevronRight, ExternalLink } from 'lucide-react';

const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';
const BUILD_NUMBER = process.env.REACT_APP_BUILD_NUMBER || '1';
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@civilxtms.com';

const platformLabel = () => {
  if (!isNativeApp()) return 'Web';
  const ua = navigator.userAgent || '';
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && 'ontouchend' in document)) return 'iPadOS';
  if (/iPhone|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  return 'Mobile';
};

const Row = ({ icon: Icon, label, hint, to, href, onClick, testid, danger }) => {
  const cls = `flex items-center gap-3 px-4 py-3.5 text-sm ${danger ? 'text-red-700' : 'text-slate-800'} hover:bg-slate-50 active:bg-slate-100 w-full text-left`;
  const inner = <><Icon className={`w-5 h-5 shrink-0 ${danger ? 'text-red-600' : 'text-[#002FA7]'}`} /><span className="flex-1"><span className="font-semibold block">{label}</span>{hint && <span className="text-xs text-slate-500">{hint}</span>}</span>{href ? <ExternalLink className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</>;
  if (href) return <a href={href} className={cls} data-testid={testid}>{inner}</a>;
  if (to) return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

const Group = ({ title, children, testid }) => (
  <section className="mb-6" data-testid={testid}>
    <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</h2>
    <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100 overflow-hidden">{children}</div>
  </section>
);

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [perm, setPerm] = useState({ location: 'unknown', camera: 'unknown', notifications: 'unknown' });

  useEffect(() => {
    const q = async (name) => { try { const r = await navigator.permissions.query({ name }); return r.state; } catch { return 'unknown'; } };
    Promise.all([q('geolocation'), q('camera')]).then(([location, camera]) => setPerm({ location, camera, notifications: typeof Notification !== 'undefined' ? Notification.permission : 'unknown' }));
  }, []);
  const permHint = (state, purpose) => ({ granted: `Allowed · ${purpose}`, denied: `Not allowed · ${purpose}`, prompt: `Asked only when needed · ${purpose}`, default: `Asked only when needed · ${purpose}` }[state] || `Asked only when needed · ${purpose}`);
  const isDriver = user?.role === 'driver';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-2xl mx-auto" style={FONT} data-testid="settings-page">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Work Sans, sans-serif' }}>Settings</h1>

        <Group title="Account" testid="settings-account">
          <Row icon={User} label="Profile" hint={user?.email} to="/profile" testid="settings-profile" />
          <Row icon={KeyRound} label="Change password" to="/profile#password" testid="settings-change-password" />
          <Row icon={Bell} label="Notifications" hint="Choose which operational alerts you receive" to="/profile#notifications" testid="settings-notifications" />
        </Group>

        <Group title="Privacy & permissions" testid="settings-privacy">
          <Row icon={MapPin} label="Location" hint={permHint(perm.location, isDriver ? 'used only while you share a trip on an active load' : 'used only by drivers sharing an active load')} to={isDriver ? '/live-tracking' : '/profile'} testid="settings-location" />
          <Row icon={Camera} label="Camera & photos" hint={permHint(perm.camera, 'used only when you tap Take Photo for receipts, POD or documents')} to={isDriver ? '/my-documents' : '/profile'} testid="settings-camera" />
          <Row icon={Bell} label="Notification permission" hint={permHint(perm.notifications, 'load assignments, dispatch updates, document requests — never ads')} to="/profile#notifications" testid="settings-notification-permission" />
          <p className="px-4 py-3 text-xs text-slate-500">You can change any permission at any time in your device's system settings. CivilX TMS never requests location, camera or notification access at launch — only when you start the related action.</p>
        </Group>

        <Group title="Legal" testid="settings-legal">
          <Row icon={Scale} label="Privacy Policy" to="/privacy-policy" testid="settings-privacy-policy" />
          <Row icon={FileText} label="Terms of Service" to="/terms" testid="settings-terms" />
        </Group>

        <Group title="Support" testid="settings-support">
          <Row icon={LifeBuoy} label="Help & Support" hint="Your company administrator is your first point of contact for dispatch and pay questions" onClick={() => navigate(user?.role === 'driver' ? '/team-chat' : '/team')} testid="settings-help" />
          <Row icon={Mail} label="Contact CivilX TMS" hint={SUPPORT_EMAIL} href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('CivilX TMS support')}`} testid="settings-contact" />
        </Group>

        <Group title="Account management" testid="settings-account-management">
          <Row icon={Trash2} label="Delete Account" hint="Permanently remove your personal account and access" to="/profile#delete-account" danger testid="settings-delete-account" />
        </Group>

        <Group title="About" testid="settings-about">
          <div className="px-4 py-4 text-sm text-slate-700 space-y-1.5">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><Info className="w-4 h-4 text-[#002FA7]" /> CivilX TMS</div>
            <div className="grid grid-cols-2 gap-y-1 text-xs">
              <span className="text-slate-500">App version</span><span data-testid="about-version">{APP_VERSION}</span>
              <span className="text-slate-500">Build number</span><span data-testid="about-build">{BUILD_NUMBER}</span>
              <span className="text-slate-500">Platform</span><span data-testid="about-platform">{platformLabel()}</span>
            </div>
            <p className="text-xs text-slate-500 pt-2">Transportation Management for Motor Carriers.</p>
            <p className="text-xs text-slate-500" data-testid="about-copyright">Copyright © 2026 Kollabsy Systems LLC. All rights reserved.</p>
          </div>
        </Group>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
